import { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../i18n';
import TranslatedText from '../components/TranslatedText';
import { auth } from '../lib/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { toast } from 'sonner';
import { ChevronDown, Upload, X, User, Building2, Ship, Users, Eye, EyeOff, Waves, Mail, GraduationCap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPES = [
  { value: 'volontario', label: 'Volontario', icon: User, description: 'Singolo cittadino' },
  { value: 'insegnante', label: 'Insegnante', icon: GraduationCap, description: 'Docente / Educatore' },
  { value: 'sub', label: 'Sub', icon: Waves, description: 'Subacqueo / Diving' },
  { value: 'peschereccio', label: 'Peschereccio', icon: Ship, description: 'Fishing for Litter' },
  { value: 'azienda', label: 'Azienda', icon: Building2, description: 'Impresa privata' },
  { value: 'associazione', label: 'Associazione', icon: Users, description: 'Organizzazione no-profit' },
  { value: 'fondazione', label: 'Fondazione', icon: Building2, description: 'Ente filantropico' },
  { value: 'ente', label: 'Ente Pubblico', icon: Building2, description: 'Comune, Provincia, Regione' },
  { value: 'scuola', label: 'Scuola', icon: GraduationCap, description: 'Istituto scolastico' }
];

// Funzione per compressione immagine
const compressImage = async (file, maxSize = 256, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve({ base64, width, height });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const Login = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  // Campi registrazione
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('volontario');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [acceptsInvites, setAcceptsInvites] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [validOrg, setValidOrg] = useState(null);
  
  const logoInputRef = useRef(null);

  const needsOrganization = !['volontario', 'sub'].includes(userType);

  // Valida codice organizzazione
  const validateInviteCode = async (code) => {
    if (!code || code.length < 3) {
      setValidOrg(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/organizations/validate/${code}`);
      setValidOrg(res.data);
    } catch (err) {
      setValidOrg(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleLogoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 256, 0.7);
      setOrganizationLogo(compressed.base64);
      setLogoPreview(compressed.base64);
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Errore compressione logo');
    } finally {
      setCompressing(false);
    }
  }, []);

  const removeLogo = useCallback(() => {
    setOrganizationLogo(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }, []);

  // Funzione per reset password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      toast.success('Email inviata! Controlla la tua casella di posta');
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('Nessun account trovato con questa email');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Email non valida');
      } else {
        toast.error('Errore nell\'invio dell\'email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignup) {
        // Validazione nome
        if (!name.trim()) {
          toast.error('Inserisci il tuo nome');
          setLoading(false);
          return;
        }
        
        // Validazione password
        if (password.length < 6) {
          toast.error('La password deve avere almeno 6 caratteri');
          setLoading(false);
          return;
        }
        
        // Crea account Firebase
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const token = await user.getIdToken();
        
        // Salva profilo completo nel backend
        await axios.post(
          `${BACKEND_URL}/api/profile/name`,
          {
            name: name.trim(),
            user_type: userType,
            organization_name: needsOrganization ? organizationName.trim() : '',
            organization_logo: needsOrganization ? organizationLogo : null,
            accepts_invites: acceptsInvites,
            invite_code: inviteCode.trim() || null  // Codice organizzazione
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Account creato con successo!');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast.success('Accesso effettuato!');
      }
    } catch (error) {
      console.error('Auth error:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email già registrata');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password troppo debole (min 6 caratteri)');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Email non valida');
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        toast.error('Email o password errati');
      } else {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationPlaceholder = () => {
    switch (userType) {
      case 'peschereccio': return 'Nome del peschereccio (es. Nettuno I)';
      case 'azienda': return 'Ragione sociale';
      case 'ente': return 'Nome ente (es. Comune di Venezia)';
      case 'associazione': return 'Nome associazione';
      case 'fondazione': return 'Nome fondazione';
      case 'insegnante': return 'Nome della scuola';
      case 'scuola': return 'Nome istituto scolastico';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md my-4">
        <div className="text-center mb-6">
          <img 
            src="/logo-respiro-blu.png" 
            alt="Respiro Blu Logo" 
            className="w-28 h-28 mx-auto mb-3 rounded-2xl object-cover"
          />
          <h1 className="text-3xl font-bold tracking-tight uppercase text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RESPIRO BLU</h1>
          <p className="text-slate-400 text-sm mt-1">Proteggi il mare, guadagna Blue Credits</p>
        </div>

        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 text-center">
            {isSignup ? 'Crea il tuo account' : 'Accedi'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                placeholder="tua@email.com"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Link Password dimenticata - solo per login */}
            {!isSignup && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Password dimenticata?
                </button>
              </div>
            )}

            {/* Campi aggiuntivi solo per registrazione */}
            {isSignup && (
              <>
                {/* Nome */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Il tuo nome *</label>
                  <input
                    data-testid="register-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder="Come ti chiami?"
                    required
                  />
                </div>

                {/* Tipo Utente - Menu a tendina */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Chi sei?</label>
                  <div className="relative">
                    <select
                      data-testid="register-user-type-select"
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 appearance-none cursor-pointer"
                    >
                      {USER_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} - {type.description}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Nome Organizzazione (se non è volontario) */}
                {needsOrganization && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">
                      {userType === 'peschereccio' ? 'Nome Peschereccio' : 'Nome Organizzazione'}
                      <span className="text-slate-500 normal-case ml-1">(opzionale)</span>
                    </label>
                    <input
                      type="text"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      placeholder={getOrganizationPlaceholder()}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                )}

                {/* Logo Organizzazione */}
                {needsOrganization && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">
                      Logo <span className="text-slate-500 normal-case">(opzionale)</span>
                    </label>
                    
                    {logoPreview ? (
                      <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
                        <span className="text-slate-300 text-sm flex-1">Logo caricato</span>
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={compressing}
                        className="w-full py-3 border-2 border-dashed border-slate-700 rounded-lg text-slate-400 hover:border-cyan-500 hover:text-cyan-400 transition-all flex items-center justify-center gap-2"
                      >
                        {compressing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                            <span>Caricamento...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Carica logo</span>
                          </>
                        )}
                      </button>
                    )}
                    <input 
                      ref={logoInputRef} 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoSelect} 
                      className="hidden" 
                    />
                  </div>
                )}

                {/* Codice Organizzazione (opzionale) */}
                <div className="mt-2">
                  <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">
                    Codice Organizzazione <span className="text-slate-500 normal-case">(opzionale)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase();
                        setInviteCode(code);
                        validateInviteCode(code);
                      }}
                      placeholder="Es: LEGAMBIENTE2024"
                      className={`w-full px-4 py-3 bg-slate-800/50 border rounded-lg text-white focus:outline-none uppercase ${
                        validOrg ? 'border-emerald-500' : inviteCode && !validatingCode ? 'border-red-500/50' : 'border-slate-700 focus:border-cyan-500'
                      }`}
                    />
                    {validatingCode && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  {validOrg && (
                    <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
                      {validOrg.organization_logo && (
                        <img src={validOrg.organization_logo} alt="" className="w-10 h-10 rounded object-contain bg-white p-1" />
                      )}
                      <span className="text-emerald-400 text-sm">
                        Ti unirai a: <strong>{validOrg.organization_name}</strong>
                      </span>
                    </div>
                  )}
                  {inviteCode && !validOrg && !validatingCode && (
                    <p className="mt-1 text-xs text-slate-500">
                      Codice non valido. Lascia vuoto se non appartieni a un'organizzazione.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Checkbox consenso inviti (solo in registrazione) */}
            {isSignup && (
              <div className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <input
                  type="checkbox"
                  id="acceptsInvites"
                  checked={acceptsInvites}
                  onChange={(e) => setAcceptsInvites(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-cyan-500 cursor-pointer"
                />
                <label htmlFor="acceptsInvites" className="text-sm text-slate-300 cursor-pointer">
                  Accetto di ricevere inviti ad attività di pulizia dalla community. 
                  <span className="text-slate-500 block text-xs mt-1">
                    Potrai modificare questa preferenza nelle impostazioni del profilo.
                  </span>
                </label>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-[0_0_15px_rgba(6,182,212,0.3)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] disabled:opacity-50 mt-2"
            >
              {loading ? 'Caricamento...' : (isSignup ? 'Registrati' : 'Accedi')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setIsSignup(!isSignup);
                // Reset campi registrazione
                setName('');
                setUserType('volontario');
                setOrganizationName('');
                setOrganizationLogo(null);
                setLogoPreview(null);
              }}
              className="text-cyan-400 hover:text-cyan-300 text-sm"
            >
              {isSignup ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-4">
          © 2026 Respiro Blu - Massimo Marchiori
        </p>
      </div>

      {/* Modal Password Dimenticata */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-cyan-500/30 rounded-2xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Mail className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Password dimenticata?
              </h3>
              <p className="text-slate-400 text-sm">
                Inserisci la tua email e ti invieremo un link per reimpostare la password.
              </p>
            </div>
            
            {resetEmailSent ? (
              <div className="text-center">
                <div className="p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl mb-4">
                  <p className="text-emerald-400 text-sm">
                    ✅ Email inviata!<br/>
                    Controlla la tua casella di posta (anche spam) e clicca sul link per reimpostare la password.
                  </p>
                </div>
                <button
                  onClick={() => { setShowForgotPassword(false); setResetEmailSent(false); }}
                  className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                >
                  Chiudi
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 mb-4"
                  placeholder="La tua email"
                  required
                />
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2 mb-3"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Invia link di reset
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full py-2 text-slate-400 hover:text-white text-sm"
                >
                  Annulla
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
